import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { RequestStatus } from 'legacy/lut/configs/service'
import type { IProgramType } from 'legacy/lut/features/partner360/configuration/models/programType'
import type { ICommitmentData } from 'legacy/lut/services/partner360/commitment/models/apiCommitmentData'
import type { IApiCommitmentStatus } from 'legacy/lut/services/partner360/commitment/models/apiCommitmentStatus'

import {
  addDownloadCaseReducers,
  addDownloadExtraReducers,
  createInitialAsyncDownloadState,
} from 'partner360/components/asyncContentWrapper/model'
import type { TAttentionStatusMode } from 'partner360/entities/attentionStatuses'
import { normalizeKeyInitiativeStatuses } from 'partner360/entities/keyInitiatives/model/status'
import {
  FIN_COCKPIT_DOWNLOAD_TYPE_PREFIX,
  PARTNER360_FIN_COCKPIT_NAMESPACE,
} from 'partner360/features/financialCockpit/constants'
import { ActualsValueTypes } from 'partner360/models/actualValueTypes'

import { fetchFinancialCockpitData } from '../api/fetchFinancialCockpitData'
import type {
  IFinancialCockpitData,
  IFinancialCockpitEltDocument,
  IFinancialCockpitQbrDocument,
} from '../api/types'
import {
  DEFAULT_CURRENCY,
  hasCockpitMandatoryPartners,
  type IPendingPbmReplacement,
  readStoredAttentionGrouping,
  storeAttentionGrouping,
} from '../lib'

import * as downloadActions from './actions'
import { requestFinancialCockpitData } from './thunks'
import {
  CockpitPeriodKey,
  ICockpitAttentionDrillStep,
  ICockpitPlanningPeriod,
  IFinancialCockpitState,
  TCockpitAttentionGrouping,
  TCockpitInitiativesView,
  TCockpitMandatoryStatus,
  TCockpitPlannedStatus,
  TCockpitRevenueAndSobView,
  TCockpitRtm,
  TMdfStatus,
} from './types'

const getSobCollectingPeriod = (
  cycles: IFinancialCockpitData['PlanningCycles']
): ICockpitPlanningPeriod | null => {
  const cycle = cycles.find(({ PlanningType }) => PlanningType === 'jbp-sob-collecting')

  return cycle ? { fYear: cycle.FYear, fQuarter: cycle.FQuarter } : null
}

const getPlanningPeriod = (
  cycles: IFinancialCockpitData['PlanningCycles']
): ICockpitPlanningPeriod | null => {
  const cycle = cycles.find(({ PlanningType }) => PlanningType === 'jbp-planning')

  return cycle ? { fYear: cycle.FYear, fQuarter: cycle.FQuarter } : null
}

const initialState: IFinancialCockpitState = {
  cockpitMode: null,
  dataRefreshVersion: 0,
  switchingOnOffering: null,
  switchingFYear: null,
  isPartnerDetailsVisible: false,
  /* The reader's own last choice, where their browser still holds it. */
  attentionGrouping: readStoredAttentionGrouping(),
  attentionLevelGrouping: null,
  attentionSearch: '',
  attentionCockpitMode: null,
  selectedAttentionStatusBit: null,
  selectedAttentionStatusOffering: null,
  attentionPath: [],
  attentionDetailPartnerId: null,
  attentionDetailOffering: null,
  isAttentionFullscreen: false,
  attentionRemindedEmails: [],
  attentionPbmReplacements: [],
  finSobData: [],
  keyInitiatives: [],
  mdfActivities: [],
  mdfSales: [],
  pendingActions: [],
  users: [],
  userInfo: null,
  partnerData: [],
  geoMappingItems: [],
  partnerFlags: [],
  partnerFlagRequests: [],
  partnerFlagApprovers: [],
  eltDocuments: [],
  qbrDocuments: [],
  planningPeriod: null,
  sobCollectingPeriod: null,
  selectedOffering: ProgramTypes.Overall,
  selectedCategory: null,
  initiativesView: 'initiatives',
  revenueAndSobView: 'detail',
  selectedPeriods: ['fullYear'],
  selectedFYear: null,
  showQuota: false,
  offeringsCatalog: [],
  offerings: [],
  actualsAndTargetsSettings: [],
  completenessData: [],
  commitmentData: [],
  commitmentStatuses: [],
  attentionDict: [],
  selectedMetric: ActualsValueTypes.None,
  selectedRtm: 'resellers',
  filter: {
    geoMappings: [],
    amplifyMemberships: [],
    partnerClasses: [],
    mdfStatus: 'All',
    plannedStatus: 'All',
    // The cockpit opens on mandatory partners, or on all of them for a manager who has none
    // (settled on the first manager load). Clear all drops it like any other criterion.
    mandatoryStatus: 'All',
    selectedPartnerIds: [],
    pbm: [],
    manager: [],
    globalPartners: [],
  },
  isMandatoryStatusFilterInitialized: false,
  availableCurrencies: [DEFAULT_CURRENCY],
  selectedCurrency: DEFAULT_CURRENCY,
  fetchingStatus: RequestStatus.NONE,
  error: null,
  download: createInitialAsyncDownloadState(),
}

const financialCockpitSlice = createSlice({
  name: PARTNER360_FIN_COCKPIT_NAMESPACE,
  initialState,
  reducers: {
    invalidateFinancialCockpitData(state) {
      fetchFinancialCockpitData.clearCache()
      state.dataRefreshVersion += 1
    },
    setCockpitSwitchingOnOffering(state, action: PayloadAction<ProgramTypes | null>) {
      state.switchingOnOffering = action.payload
    },
    setCockpitPartnerDetailsVisible(state, action: PayloadAction<boolean>) {
      state.isPartnerDetailsVisible = action.payload
    },
    setCockpitOffering(state, action: PayloadAction<ProgramTypes>) {
      if (state.selectedOffering !== action.payload) {
        state.selectedCategory = null
        state.selectedMetric = ActualsValueTypes.None
      }
      state.selectedOffering = action.payload
    },
    setCockpitCategory(state, action: PayloadAction<string | null>) {
      state.selectedCategory = action.payload
    },
    /**
     * Regroups the level the drill-down stands on and nothing above it. The first level under a
     * status is the reader's standing choice, so it is kept in the browser; deeper levels are
     * picked for the group they belong to.
     */
    setCockpitAttentionGrouping(state, action: PayloadAction<TCockpitAttentionGrouping>) {
      state.attentionSearch = ''

      if (state.attentionPath.length === 0) {
        state.attentionGrouping = action.payload
        storeAttentionGrouping(action.payload)
        return
      }

      state.attentionLevelGrouping = action.payload
    },
    /** What the reader is looking for on the level in view. It belongs to that level, so every
     *  step of the drill-down below clears it. */
    setCockpitAttentionSearch(state, action: PayloadAction<string>) {
      state.attentionSearch = action.payload
    },
    /**
     * Entering a cockpit drops the other cockpit's drill-down. The single-partner and the
     * multiple-partner catalogs share their `BitValue`s, so a status picked in one of them would
     * otherwise open a detail in the other that the reader never asked for.
     */
    resetCockpitAttentionDrill(state, action: PayloadAction<TAttentionStatusMode>) {
      state.attentionCockpitMode = action.payload
      state.attentionSearch = ''
      state.selectedAttentionStatusBit = null
      state.selectedAttentionStatusOffering = null
      state.attentionPath = []
      state.attentionLevelGrouping = null
      state.attentionDetailPartnerId = null
      state.attentionDetailOffering = null
    },
    /** Picking or dropping a status restarts the drill-down: its path belongs to the status that
     *  was open, not to the next one. */
    setCockpitSelectedAttentionStatus(
      state,
      action: PayloadAction<{ bitValue: number; offering?: string | null } | null>
    ) {
      state.selectedAttentionStatusBit = action.payload?.bitValue ?? null
      state.selectedAttentionStatusOffering = action.payload?.offering ?? null
      state.attentionSearch = ''
      state.attentionPath = []
      state.attentionLevelGrouping = null
      state.attentionDetailPartnerId = null
      state.attentionDetailOffering = null
    },
    /** Opens the status in view for one partner of its partner level, to be worked on there. */
    openCockpitAttentionPartner(
      state,
      action: PayloadAction<{ partnerId: string; offering: string | null }>
    ) {
      state.attentionDetailPartnerId = action.payload.partnerId
      state.attentionDetailOffering = action.payload.offering
    },
    closeCockpitAttentionPartner(state) {
      state.attentionDetailPartnerId = null
      state.attentionDetailOffering = null
    },
    /** Switches the open partner to another status it holds, keeping the partner open. */
    switchCockpitAttentionPartnerStatus(
      state,
      action: PayloadAction<{ bitValue: number; offering: string | null }>
    ) {
      state.selectedAttentionStatusBit = action.payload.bitValue
      state.attentionDetailOffering = action.payload.offering
      state.attentionSearch = ''
      state.attentionPath = []
      state.attentionLevelGrouping = null
    },
    /** Walks into a group of the level in view; the level it opens starts on its own default. */
    enterCockpitAttentionGroup(state, action: PayloadAction<ICockpitAttentionDrillStep>) {
      state.attentionSearch = ''
      state.attentionDetailPartnerId = null
      state.attentionDetailOffering = null
      state.attentionPath.push(action.payload)
      state.attentionLevelGrouping = null
    },
    /** Steps back to the level `index` steps deep, shown in the grouping it was left in. */
    goToCockpitAttentionLevel(state, action: PayloadAction<number>) {
      const step = state.attentionPath[action.payload]

      if (!step) {
        return
      }

      state.attentionSearch = ''
      state.attentionDetailPartnerId = null
      state.attentionDetailOffering = null
      state.attentionPath = state.attentionPath.slice(0, action.payload)
      state.attentionLevelGrouping = action.payload > 0 ? step.grouping : null
    },
    /** Replaces the whole path, e.g. from the full-screen tree. */
    setCockpitAttentionPath(state, action: PayloadAction<ICockpitAttentionDrillStep[]>) {
      state.attentionSearch = ''
      state.attentionDetailPartnerId = null
      state.attentionDetailOffering = null
      state.attentionPath = action.payload
      state.attentionLevelGrouping = null
    },
    setCockpitAttentionFullscreen(state, action: PayloadAction<boolean>) {
      state.isAttentionFullscreen = action.payload
    },
    markCockpitAttentionReminded(state, action: PayloadAction<string[]>) {
      state.attentionRemindedEmails = [
        ...new Set([...state.attentionRemindedEmails, ...action.payload]),
      ]
    },
    /** Replaces what is known of the partners' open replacements with what was just learned. */
    setCockpitAttentionPbmReplacements(
      state,
      action: PayloadAction<{ partnerIds: string[]; replacements: IPendingPbmReplacement[] }>
    ) {
      const { partnerIds, replacements } = action.payload
      state.attentionPbmReplacements = [
        ...state.attentionPbmReplacements.filter(
          ({ partnerId }) => !partnerIds.includes(partnerId)
        ),
        ...replacements,
      ]
    },
    addCockpitAttentionPbmReplacements(state, action: PayloadAction<IPendingPbmReplacement[]>) {
      state.attentionPbmReplacements = [...state.attentionPbmReplacements, ...action.payload]
    },
    setCockpitInitiativesView(state, action: PayloadAction<TCockpitInitiativesView>) {
      state.initiativesView = action.payload
    },
    setCockpitRevenueAndSobView(state, action: PayloadAction<TCockpitRevenueAndSobView>) {
      state.revenueAndSobView = action.payload
    },
    updateCockpitOffering(
      state,
      action: PayloadAction<{ partnerId: string; offering: IProgramType }>
    ) {
      const partner = state.offeringsCatalog.find(
        (it) => it.partnerId === action.payload.partnerId.trim()
      )
      const index = partner?.offerings.findIndex(
        ({ programType }) => programType === action.payload.offering.programType
      )

      if (partner && index !== undefined && index !== -1) {
        partner.offerings[index] = action.payload.offering
      }
    },
    setCockpitCommitmentDataForProgramType(
      state,
      action: PayloadAction<{
        partnerId: string
        programType: ProgramTypes
        items: ICommitmentData[]
      }>
    ) {
      const partnerId = action.payload.partnerId.trim()
      const offering = action.payload.programType.trim()

      state.commitmentData = [
        ...state.commitmentData.filter(
          (item) => !(item.PartnerId.trim() === partnerId && item.ProgramType.trim() === offering)
        ),
        ...action.payload.items,
      ]
    },
    setCockpitCommitmentStatuses(state, action: PayloadAction<IApiCommitmentStatus[]>) {
      state.commitmentStatuses = action.payload
    },
    setCockpitPeriods(state, action: PayloadAction<CockpitPeriodKey[]>) {
      state.selectedPeriods = action.payload
    },
    setCockpitFYear(state, action: PayloadAction<number>) {
      state.selectedFYear = action.payload
    },
    setCockpitShowQuota(state, action: PayloadAction<boolean>) {
      state.showQuota = action.payload
    },
    setCockpitMetric(state, action: PayloadAction<ActualsValueTypes>) {
      state.selectedMetric = action.payload
    },
    setCockpitCurrency(state, action: PayloadAction<string>) {
      state.selectedCurrency = action.payload
    },
    setCockpitRtm(state, action: PayloadAction<TCockpitRtm>) {
      state.selectedRtm = action.payload
    },
    setCockpitFilterGeoMappings(state, action: PayloadAction<string[]>) {
      state.filter.geoMappings = action.payload
    },
    setCockpitFilterAmplifyMemberships(state, action: PayloadAction<string[]>) {
      state.filter.amplifyMemberships = action.payload
    },
    setCockpitFilterPartnerClasses(state, action: PayloadAction<string[]>) {
      state.filter.partnerClasses = action.payload
    },
    setCockpitFilterMdfStatus(state, action: PayloadAction<TMdfStatus>) {
      state.filter.mdfStatus = action.payload
    },
    setCockpitFilterPlannedStatus(state, action: PayloadAction<TCockpitPlannedStatus>) {
      state.filter.plannedStatus = action.payload
    },
    setCockpitFilterMandatoryStatus(state, action: PayloadAction<TCockpitMandatoryStatus>) {
      state.filter.mandatoryStatus = action.payload
    },
    setCockpitSelectedPartnerIds(state, action: PayloadAction<string[]>) {
      state.filter.selectedPartnerIds = action.payload
    },
    setCockpitFilterPbm(state, action: PayloadAction<string[]>) {
      state.filter.pbm = action.payload
    },
    setCockpitFilterManager(state, action: PayloadAction<string[]>) {
      state.filter.manager = action.payload
    },
    setCockpitFilterGlobalPartners(state, action: PayloadAction<string[]>) {
      state.filter.globalPartners = action.payload
    },
    resetCockpitFilter(state) {
      state.filter = {
        geoMappings: [],
        amplifyMemberships: [],
        partnerClasses: [],
        mdfStatus: 'All',
        plannedStatus: 'All',
        mandatoryStatus: 'All',
        selectedPartnerIds: [],
        pbm: [],
        manager: [],
        globalPartners: [],
      }
    },
    addCockpitEltDocument(
      state,
      action: PayloadAction<{ partnerId: string; document: IFinancialCockpitEltDocument }>
    ) {
      const partnerId = action.payload.partnerId.trim()
      const group = state.eltDocuments.find((item) => item.PartnerId.trim() === partnerId)

      if (group) {
        group.ELTDocuments = [action.payload.document, ...group.ELTDocuments]
      } else {
        state.eltDocuments.push({ PartnerId: partnerId, ELTDocuments: [action.payload.document] })
      }
    },
    addCockpitQbrDocument(
      state,
      action: PayloadAction<{ partnerId: string; document: IFinancialCockpitQbrDocument }>
    ) {
      const partnerId = action.payload.partnerId.trim()
      const group = state.qbrDocuments.find((item) => item.PartnerId.trim() === partnerId)

      if (group) {
        group.QBRDocuments = [action.payload.document, ...group.QBRDocuments]
      } else {
        state.qbrDocuments.push({ PartnerId: partnerId, QBRDocuments: [action.payload.document] })
      }
    },
    removeCockpitEltDocument(state, action: PayloadAction<{ partnerId: string; entryId: number }>) {
      const partnerId = action.payload.partnerId.trim()
      const group = state.eltDocuments.find((item) => item.PartnerId.trim() === partnerId)
      if (group)
        group.ELTDocuments = group.ELTDocuments.filter(
          (doc) => doc.EntryId !== action.payload.entryId
        )
    },
    removeCockpitQbrDocument(state, action: PayloadAction<{ partnerId: string; entryId: number }>) {
      const partnerId = action.payload.partnerId.trim()
      const group = state.qbrDocuments.find((item) => item.PartnerId.trim() === partnerId)
      if (group)
        group.QBRDocuments = group.QBRDocuments.filter(
          (doc) => doc.EntryId !== action.payload.entryId
        )
    },
  },
  extraReducers(builder) {
    builder
      .addCase(requestFinancialCockpitData.pending, (state, action) => {
        state.cockpitMode = action.meta.arg.partnerId ? 'partner' : 'manager'
        state.fetchingStatus = RequestStatus.PENDING
        state.error = null
        state.switchingFYear = action.meta.arg.keepOnScreen
          ? { fYear: action.meta.arg.fYear, requestId: action.meta.requestId }
          : null
      })
      .addCase(requestFinancialCockpitData.rejected, (state, action) => {
        if (state.switchingFYear?.requestId === action.meta.requestId) {
          state.switchingFYear = null
        }
        state.fetchingStatus = RequestStatus.REJECTED
        state.error = action.error.message ?? 'Failed to fetch financial cockpit data'
      })
      .addCase(requestFinancialCockpitData.fulfilled, (state, action) => {
        if (state.switchingFYear?.requestId === action.meta.requestId) {
          state.switchingFYear = null
        }
        state.fetchingStatus = RequestStatus.FULFILLED
        state.finSobData = action.payload.PartnerFinSobData
        state.keyInitiatives = action.payload.KeyInitiatives.flatMap(({ KeyInitiatives }) =>
          normalizeKeyInitiativeStatuses(KeyInitiatives)
        )
        state.mdfActivities = action.payload.MDFActivities.flatMap(
          ({ MDFActivities }) => MDFActivities
        )
        state.mdfSales = action.payload.MDFSales ?? []
        state.pendingActions = action.payload.PBMActions
        /* The payload sends the requesting user as a one-element array; the array shape stops
           in the API mapper, so the slice takes the profile as it is. */
        state.userInfo = action.payload.UserInfo
        state.users = action.payload.Users.flatMap(({ PartnerId, Offering, Users }) =>
          Users.map((user) => ({ ...user, PartnerId, Offering }))
        )
        state.partnerData = action.payload.PartnerData
        state.geoMappingItems = action.payload.GeoMapping
        state.partnerFlags = action.payload.PartnerFlags ?? []
        state.partnerFlagRequests = action.payload.PartnerFlagRequests ?? []
        state.partnerFlagApprovers = action.payload.PartnerFlagApprovers ?? []
        state.eltDocuments = action.payload.ELTDocuments
        state.qbrDocuments = action.payload.QBRDocuments
        state.planningPeriod = getPlanningPeriod(action.payload.PlanningCycles)
        state.sobCollectingPeriod = getSobCollectingPeriod(action.payload.PlanningCycles)
        state.offeringsCatalog = action.payload.PartnerOfferingsCatalog
        state.offerings = action.payload.Offerings
        state.actualsAndTargetsSettings = action.payload.ActualsAndTargetsSettings
        state.completenessData = action.payload.CompletenessData
        state.commitmentData = action.payload.CommitmentData.flatMap(
          ({ CommitmentData }) => CommitmentData
        )
        state.commitmentStatuses = action.payload.CommitmentStatus.flatMap(
          ({ CommitmentStatus }) => CommitmentStatus
        )
        state.attentionDict = action.payload.AttentionDict ?? []
        state.availableCurrencies = action.payload.AvailableCurrencies
        state.error = null

        /* A manager none of whose partners is mandatory opens on all of them instead. Only the
           first manager load decides; later loads keep whatever the reader has picked since. */
        if (!action.meta.arg.partnerId && !state.isMandatoryStatusFilterInitialized) {
          state.isMandatoryStatusFilterInitialized = true

          if (
            state.filter.mandatoryStatus === 'Mandatory' &&
            !hasCockpitMandatoryPartners(
              action.payload.PartnerData,
              action.payload.CompletenessData
            )
          ) {
            state.filter.mandatoryStatus = 'All'
          }
        }
      })

    addDownloadCaseReducers<IFinancialCockpitState>(builder, downloadActions)
    addDownloadExtraReducers<IFinancialCockpitState>(builder, FIN_COCKPIT_DOWNLOAD_TYPE_PREFIX)
  },
})

export const {
  addCockpitEltDocument,
  addCockpitQbrDocument,
  invalidateFinancialCockpitData,
  setCockpitSwitchingOnOffering,
  removeCockpitEltDocument,
  removeCockpitQbrDocument,
  setCockpitPartnerDetailsVisible,
  setCockpitInitiativesView,
  setCockpitRevenueAndSobView,
  setCockpitOffering,
  setCockpitCategory,
  resetCockpitAttentionDrill,
  setCockpitAttentionGrouping,
  setCockpitAttentionSearch,
  enterCockpitAttentionGroup,
  goToCockpitAttentionLevel,
  openCockpitAttentionPartner,
  closeCockpitAttentionPartner,
  switchCockpitAttentionPartnerStatus,
  markCockpitAttentionReminded,
  setCockpitAttentionPbmReplacements,
  addCockpitAttentionPbmReplacements,
  setCockpitAttentionFullscreen,
  setCockpitAttentionPath,
  setCockpitSelectedAttentionStatus,
  updateCockpitOffering,
  setCockpitCommitmentDataForProgramType,
  setCockpitCommitmentStatuses,
  setCockpitPeriods,
  setCockpitFYear,
  setCockpitShowQuota,
  setCockpitMetric,
  setCockpitCurrency,
  setCockpitRtm,
  setCockpitFilterGeoMappings,
  setCockpitFilterAmplifyMemberships,
  setCockpitFilterPartnerClasses,
  setCockpitFilterMdfStatus,
  setCockpitFilterPlannedStatus,
  setCockpitFilterMandatoryStatus,
  setCockpitSelectedPartnerIds,
  setCockpitFilterPbm,
  setCockpitFilterManager,
  setCockpitFilterGlobalPartners,
  resetCockpitFilter,
} = financialCockpitSlice.actions

export const financialCockpitSliceReducer = financialCockpitSlice.reducer

export * from './actions'
